import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import {
  documentConfig,
  DocumentLabel,
  DocumentType,
  VariantType,
  type DocumentCardProps,
} from './documentCard/DocumentCard.types'
import type { DocumentPageRequest, IndicateurTbd } from './IndicateurTdb.types'
import { loadRecherche } from '@/features/services/recherche'
import { useAppStateStore } from '@/features/application'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { injectLoadRechercheService } from '@/features/interface/recherches/rechercheRoutingServiceProvider'
import { useAjouteDemande } from '@/features/interface/demande/ajouter/useAjouteDemande'
import { useDemandeRoutingService } from '@/features/interface/demande/shared'
import { useRechercheFactureActions } from '@/features/interface/recherches/facture/useRechercheFactureActions'
import type { PagedRequest } from '@/features/interface/recherches/storeRechercheBuilder'
import type { TypePermission } from '@/features/domaine/types/opco/Permissions/TypePermission'
import { TRANSITION_CANCELED } from '@/features/domaine/machine-etats/types'

export type DocumentCardByType = Record<DocumentType, DocumentCardProps>

export const useIndicateurTdbStore = defineStore('indicateurTdb', () => {
  const indicateurTdb = ref<IndicateurTbd>()
  const SLOW_THRESHOLD = 5000
  const appState = useAppStateStore()
  const { redirectSelfeFacture } = useRechercheFactureActions()

  const permissionsMap: Record<DocumentType, TypePermission | TypePermission[]> = {
    dossiers: ['AF', 'CPRO', 'CAPP', 'PROA'],
    factures: 'Facture',
  }

  function createEmptyCard(type: DocumentType): DocumentCardProps {
    return {
      type,
      permissions: permissionsMap[type],
      indicateurs: {
        primary: {
          count: 0,
          loading: true,
          slowLoading: false,
          error: false,
          titre: undefined,
        },
        secondary: {
          count: 0,
          loading: true,
          slowLoading: false,
          error: false,
          titre: undefined,
        },
      },
    }
  }

  const documentCards = ref<DocumentCardByType>(
    Object.fromEntries(DocumentType.map((type) => [type, createEmptyCard(type)])) as DocumentCardByType,
  )

  function createIndicateur(
    label: DocumentLabel,
    query: object,
    routeName: string,
    typeRecherche: 'demande' | 'dossier' | 'facture',
  ) {
    return {
      label,
      pageRequest: {
        query,
        pagination: { pageNumber: 1, pageSize: 0 },
        orderBy: [],
        route: { name: routeName, params: { context: '' } },
      },
      typeRecherche,
    }
  }

  function getTdbIndicateur(): IndicateurTbd {
    const adherentStatus = appState.contextCourant === TypesContextesAutorisesEnum.Adherent
    return {
      DossierIndicateur1: createIndicateur(
        adherentStatus ? DocumentLabel.DemandeAValider : DocumentLabel.DemandeAccepterRefuser,
        adherentStatus ? { statuts: ['AReprendreAdherent'] } : { statuts: ['RefusAdherent', 'RepriseAdherent'] },
        'recherche-demande',
        'demande',
      ),
      DossierIndicateur2: createIndicateur(
        DocumentLabel.DossiersCompleter,
        { statuts: ['AttentePiecesInstruction', 'AttentePiecesReglement'] },
        'recherche-dossier',
        'dossier',
      ),
      FactureIndicateur1: createIndicateur(
        DocumentLabel.NouvellesFactures,
        { statuts: ['EnCoursDAnalyse', 'Transmis'], periodeDateReception: 'DerniersTrenteJours' },
        'factures',
        'facture',
      ),
      FactureIndicateur2: createIndicateur(
        DocumentLabel.FacturesRegulariser,
        { statuts: ['AttentePiecesReglement'] },
        'factures',
        'facture',
      ),
    }
  }

  async function fetchIndicator(type: DocumentType, variant: VariantType) {
    const tbd = indicateurTdb.value!
    const config = documentConfig[type][variant]
    const indicator = documentCards.value[type].indicateurs[variant]

    const hasContext = appState.contextCourant !== TypesContextesAutorisesEnum.Aucun

    if (!hasContext) {
      indicator.count = 0
      indicator.titre = config.getLabel(tbd)
      indicator.loading = false
      return
    }

    indicator.loading = true
    indicator.error = false
    indicator.slowLoading = false
    const slowTimer = setTimeout(() => {
      indicator.slowLoading = true
    }, SLOW_THRESHOLD)

    try {
      const result = await loadRecherche<ReturnType<typeof config.getPageRequest>, DocumentPageRequest>(
        config.route,
        config.getPageRequest(tbd) as PagedRequest<DocumentPageRequest>,
        {
          sablier: false,
          dialogMode: 'silent',
          appCache: true,
          appCacheAddKey: [appState.contextCourant, appState.utilisateur?.emailAffichage],
          appCacheDurationEnSecondes: 300,
        },
      )
      indicator.count = result?.totalRecords ?? 0
      indicator.titre = config.getLabel(tbd)
    } catch (_error) {
      indicator.error = true
    } finally {
      clearTimeout(slowTimer)
      indicator.loading = false
      indicator.slowLoading = false
    }
  }

  async function loadData() {
    indicateurTdb.value = getTdbIndicateur()

    const tasks = []
    for (const docType of DocumentType) {
      for (const variant of VariantType) {
        tasks.push(fetchIndicator(docType, variant))
      }
    }

    await Promise.all(tasks)
  }

  async function redirectionDocument(type: DocumentType, variant: VariantType) {
    const indicatorKey = documentConfig[type][variant].labelKey as keyof IndicateurTbd
    const selectedIndicator = indicateurTdb.value![indicatorKey]
    const { loadPageWithPagedRequest } = injectLoadRechercheService(selectedIndicator.typeRecherche)
    loadPageWithPagedRequest(selectedIndicator.pageRequest)
  }

  const ajoutDocument = async (type: DocumentType) => {
    const actions = {
      dossiers: async () => {
        const storeAddDemande = useAjouteDemande()
        try {
          const demande = await storeAddDemande.showAjoute()
          useDemandeRoutingService().navigateToWithId(demande.dispositif, demande.id)
        } catch (err) {
          if (err !== TRANSITION_CANCELED) throw err
          console.error(err)
        }
      },
      factures: async () => {
        redirectSelfeFacture()
      },
    }

    await actions[type]()
  }

  watch(() => ([appState.contextCourant, appState.utilisateur?.emailAffichage]), loadData, {
    immediate: true,
    deep: true,
  })

  return {
    documentCards,
    loadData,
    redirectSelfeFacture,
    ajoutDocument,
    redirectionDocument,
    fetchIndicator,
  }
})
